import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import { useEffect } from 'react';

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
  readOnly?: boolean;
}

/**
 * Rich text editor oparty o Tiptap (ProseMirror) — z podkreśleniami diff
 * highlight dla weryfikacji draftów AI.
 */
export function TiptapEditor({ value, onChange, minHeight = '400px', readOnly = false }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight.configure({ multicolor: true }),
    ],
    content: value,
    editable: !readOnly,
    onUpdate: ({ editor }) => { onChange(editor.getHTML()); },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return <div className="text-ink-400">Ładowanie edytora…</div>;

  return (
    <div className="border border-ink-200 rounded-md">
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-1 p-2 border-b border-ink-100 bg-ink-50">
          <div className="flex gap-1 items-center">
            <button type="button" className={btnCls(editor.isActive('bold'))}
              onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></button>
            <button type="button" className={btnCls(editor.isActive('italic'))}
              onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></button>
            <button type="button" className={btnCls(editor.isActive('strike'))}
              onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></button>
          </div>
          <span className="w-px h-5 bg-ink-200" />
          <div className="flex gap-1 items-center">
            <button type="button" className={btnCls(editor.isActive('heading', { level: 2 }))}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</button>
            <button type="button" className={btnCls(editor.isActive('heading', { level: 3 }))}
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>H3</button>
          </div>
          <span className="w-px h-5 bg-ink-200" />
          <div className="flex gap-1 items-center">
            <button type="button" className={btnCls(editor.isActive('bulletList'))}
              onClick={() => editor.chain().focus().toggleBulletList().run()}>• Lista</button>
            <button type="button" className={btnCls(editor.isActive('orderedList'))}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. Num</button>
          </div>
          <span className="w-px h-5 bg-ink-200" />
          <div className="flex gap-1 items-center">
            <button type="button" className={btnCls(editor.isActive('highlight', { color: '#d1f5e7' }))}
              onClick={() => editor.chain().focus().toggleHighlight({ color: '#d1f5e7' }).run()}>+ Zielony</button>
            <button type="button" className={btnCls(editor.isActive('highlight', { color: '#fdecc3' }))}
              onClick={() => editor.chain().focus().toggleHighlight({ color: '#fdecc3' }).run()}>~ Żółty</button>
          </div>
          <div className="flex gap-1 items-center ml-auto">
            <button type="button" className={btnCls(false)} title="Cofnij"
              onClick={() => editor.chain().focus().undo().run()}>↶</button>
            <button type="button" className={btnCls(false)} title="Ponów"
              onClick={() => editor.chain().focus().redo().run()}>↷</button>
          </div>
        </div>
      )}
      <EditorContent
        editor={editor}
        className="tiptap-editor-content prose prose-sm max-w-none px-4 py-3"
        style={{ minHeight }}
      />
    </div>
  );
}

function btnCls(active: boolean): string {
  return 'px-2 py-0.5 text-[12px] rounded border '
    + (active ? 'bg-ink-900 text-white border-ink-900' : 'bg-white text-ink-700 border-ink-200 hover:bg-ink-50');
}
